import { useEffect } from 'react';

import {
  initializeAttribution,
  isFirstVisit,
} from '@/utils/attributionTracking';
import { recordLeadJourneyPath } from '@/utils/leadJourneyTracking';
import { trackRefClickOnce } from '@/utils/refClickBeacon';

interface AttributionTrackingOptions {
  enabled?: boolean;
  pathname?: string | null;
}

export const useAttributionTracking = (
  options: AttributionTrackingOptions = {}
): void => {
  const { enabled = true, pathname = null } = options;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Small delay to ensure URL is fully loaded and referrer is available
    const timeoutId = setTimeout(() => {
      try {
        const isFirst = isFirstVisit();
        initializeAttribution();
        // After initializeAttribution so the stored last-touch ref is already
        // written: if the beacon request fails, the lead is still attributed.
        trackRefClickOnce();

        if (isFirst && import.meta.env.DEV) {
          console.warn('First visit detected - attribution data captured');
        }
      } catch (error) {
        console.error('Error initializing attribution tracking:', error);
      }
    }, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    try {
      recordLeadJourneyPath(pathname ?? undefined);
    } catch (error) {
      console.error('Error recording lead journey path:', error);
    }
  }, [enabled, pathname]);
};
